import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import PreLaunchBanner from '../../components/common/PreLaunchBanner';

export default function RiderRegisterPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { registerRider } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.phone.trim()) errs.phone = 'Phone number is required';
    else if (!/^(\+61|04)\d{8,9}$/.test(form.phone.replace(/\s/g, ''))) errs.phone = 'Enter a valid Australian mobile number';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/.test(form.password))
      errs.password = 'Include uppercase, lowercase, and a number';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await registerRider({
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.replace(/\s/g, ''),
        referralCode: form.referralCode || null,
      });
      if (form.referralCode) {
        if (result?.referralResult?.success) {
          toast.success('Account created! Referral applied.');
        } else if (result?.referralResult?.reason && result.referralResult.reason !== 'no_code_provided') {
          toast.success('Account created! (Referral code could not be applied — registration not affected.)');
        } else {
          toast.success('Account created! Verify your phone number.');
        }
      } else {
        toast.success('Account created! Verify your phone number.');
      }
      navigate('/verify-otp');
    } catch (err) {
      const code = err?.code || err?.cause?.code || err?.error?.code || '';
      const friendlyMessages = {
        'auth/email-already-in-use': { field: 'email', text: 'An account with this email already exists. Sign in or use a different email.' },
        'app/phone-already-in-use': { field: 'phone', text: 'That mobile number is already registered to another account. Use a different number or sign in.' },
        'auth/invalid-email': { field: 'email', text: 'That email address is not valid. Please check and try again.' },
        'auth/weak-password': { field: 'password', text: 'That password is too weak. Use at least 8 characters with letters and numbers.' },
        'auth/operation-not-allowed': { toast: 'Registration is temporarily disabled. Please try again later or contact support.' },
        'auth/too-many-requests': { toast: 'Too many attempts. Please wait a few minutes and try again.' },
        'auth/network-request-failed': { toast: 'Network error. Check your internet connection and try again.' },
        'auth/internal-error': { toast: 'Something went wrong on our end. Please try again in a moment.' }
      };
      const mapped = friendlyMessages[code];
      if (mapped?.field) {
        setErrors({ [mapped.field]: mapped.text });
      } else if (mapped?.toast) {
        toast.error(mapped.toast);
      } else {
        console.error('Registration error (unmapped):', { code, message: err?.message, raw: err });
        toast.error('Registration failed. Please try again. If the problem persists, contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* LEFT PANEL — form */}
      <div className="flex-1 bg-brand-cream flex items-center justify-center px-5 py-8 sm:px-6 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md">
          <PreLaunchBanner variant="full" />

          <Link to="/" className="flex items-center gap-3 mt-6 mb-8 md:mb-12">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center font-extrabold text-lg text-brand-dark bg-gradient-to-br from-brand-primary to-brand-primary-light shadow-brand">
              R
            </div>
            <span className="font-serif text-2xl text-brand-dark">Reservo</span>
          </Link>

          <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2 tracking-tight">
            Create your account
          </h1>
          <p className="text-body text-base leading-relaxed mb-6">
            Join Reservo to book pre-booked rides with upfront pricing.
          </p>

          <form onSubmit={handleSubmit} className="mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group">
                <label className="form-label">First name</label>
                <input
                  type="text"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  autoComplete="given-name"
                />
                {errors.firstName && <div className="form-error">{errors.firstName}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Last name</label>
                <input
                  type="text"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  autoComplete="family-name"
                />
                {errors.lastName && <div className="form-error">{errors.lastName}</div>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email address</label>
              <input
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange('email')}
                autoComplete="email"
              />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Mobile number</label>
              <input
                type="tel"
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="04XX XXX XXX"
                value={form.phone}
                onChange={handleChange('phone')}
                autoComplete="tel"
              />
              {errors.phone && <div className="form-error">{errors.phone}</div>}
              <div className="text-muted text-xs mt-1.5">Australian mobile number for SMS verification.</div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={form.password}
                onChange={handleChange('password')}
                autoComplete="new-password"
              />
              {errors.password && <div className="form-error">{errors.password}</div>}
              {!errors.password && form.password && form.password.length >= 8 && /(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/.test(form.password) && (
                <div className="text-success text-xs mt-1.5 font-semibold">Strong password</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm password</label>
              <input
                type="password"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                autoComplete="new-password"
              />
              {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
              {!errors.confirmPassword && form.confirmPassword && form.password === form.confirmPassword && (
                <div className="text-success text-xs mt-1.5 font-semibold">✓ Passwords match</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Referral code <span className="text-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                className="form-input uppercase tracking-wider"
                placeholder="8-CHARACTER CODE FROM A RESERVO DRIVER"
                value={form.referralCode}
                onChange={handleChange('referralCode')}
                maxLength={20}
              />
              <div className="text-muted text-xs mt-1.5">
                Have a code from a Reservo driver? Enter it here so they get credit when you complete your first trip.
              </div>
            </div>

            <p className="text-body text-xs leading-relaxed mb-5">
              By creating an account, you agree to Reservo's{' '}
              <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-brand-primary font-semibold">Terms of Service</Link>{' '}
              and{' '}
              <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-primary font-semibold">Privacy Policy</Link>.
            </p>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : 'Create Account'}
            </button>
          </form>

          <p className="text-body text-sm text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-primary font-semibold">Sign in</Link>
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — marketing cards */}
      <div className="flex-1 bg-brand-dark flex items-center justify-center px-5 py-8 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md flex flex-col gap-6">
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Pre-booked</div>
            <div className="text-white/50 text-sm">Lock in your ride hours or days ahead</div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Upfront pricing</div>
            <div className="text-white/50 text-sm">Know the exact fare before you book</div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">NSW Licensed</div>
            <div className="text-white/50 text-sm">All drivers are PT authorised</div>
          </div>
        </div>
      </div>
    </div>
  );
}